/* src/components/EmbedSettings.jsx
 * Embed code generator with customization options
 */

import React, { useState, useEffect } from 'react';

const PLATFORM_INSTRUCTIONS = {
  wordpress: {
    label: 'WordPress',
    steps: 'Edit the page → click Add Block (+) → search for and choose Custom HTML (not Paragraph or Text) → paste the code → click Update.',
  },
  squarespace: {
    label: 'Squarespace',
    steps: 'Edit the section → click the + to add a block → choose Code → paste the code → click Apply, then Save.',
  },
  wix: {
    label: 'Wix',
    steps: 'In the editor click Add (+) → Embed Code → Embed HTML → paste the code into the HTML iframe box → click Apply → Publish.',
  },
  shopify: {
    label: 'Shopify',
    steps: 'Online Store → Themes → Edit code, or add a Custom Liquid section in the page editor → paste the code → Save.',
  },
  webflow: {
    label: 'Webflow',
    steps: 'In the Designer add an Embed element (Components panel) → double-click it → paste the code → click Save & Close → Publish.',
  },
  other: {
    label: 'Other (raw HTML)',
    steps: 'Paste the code anywhere inside <body>, ideally just before the closing </body> tag.',
  },
};

export default function EmbedSettings({ formId, embedSettings, onSave, onClose }) {
  const [settings, setSettings] = useState({
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    responsive: true,
    border: true,
    borderRadius: '8px',
    padding: '2rem',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    embedType: 'script', // 'script' or 'iframe'
    noScroll: false, // when true, form has no scrolling area (overflow hidden)
    ...embedSettings,
  });

  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState('wordpress');

  useEffect(() => {
    if (embedSettings) {
      setSettings({ ...settings, ...embedSettings });
    }
  }, [embedSettings]);

  function updateSetting(key, value) {
    setSettings({ ...settings, [key]: value });
  }

  function generateScriptEmbed() {
    const baseUrl = window.location.origin;
    // Build URL with embed settings as query params
    const params = new URLSearchParams({
      embed: 'true',
      width: settings.width,
      maxWidth: settings.maxWidth,
      padding: settings.padding,
      borderRadius: settings.borderRadius,
      responsive: settings.responsive.toString(),
      noScroll: settings.noScroll.toString(),
    });
    
    return `<script src="${baseUrl}/embed.js" data-form-id="${formId}"></script>
<div id="smartform-${formId}" style="width: ${settings.width}; max-width: ${settings.maxWidth}; margin: ${settings.margin};"></div>
<script>
  (function() {
    var container = document.getElementById('smartform-${formId}');
    if (container) {
      var iframe = document.createElement('iframe');
      var src = '${baseUrl}/form/${formId}?${params.toString()}';
      // Forward ad click IDs + UTMs from this page into the form (attribution)
      try {
        var attrKeys = ['gclid','gbraid','wbraid','fbclid','ttclid','msclkid','utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
        var pageParams = new URLSearchParams(window.location.search);
        attrKeys.forEach(function(k){ var v = pageParams.get(k); if (v) src += '&' + k + '=' + encodeURIComponent(v); });
      } catch (e) {}
      iframe.src = src;
      iframe.style.width = '100%';
      iframe.style.height = '${settings.height === 'auto' ? '600px' : settings.height}';
      iframe.style.border = 'none';
      iframe.style.borderRadius = '${settings.borderRadius}';
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('loading', 'lazy');
      container.appendChild(iframe);
    }
  })();
</script>`;
  }

  function generateIframeEmbed() {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      embed: 'true',
      width: settings.width,
      maxWidth: settings.maxWidth,
      padding: settings.padding,
      borderRadius: settings.borderRadius,
      responsive: settings.responsive.toString(),
      noScroll: settings.noScroll.toString(),
    });
    const width = settings.width === '100%' ? '100%' : `${settings.width}`;
    const height = settings.height === 'auto' ? '600' : `${settings.height}`;
    
    return `<iframe 
  src="${baseUrl}/form/${formId}?${params.toString()}" 
  width="${width}" 
  height="${height}" 
  frameborder="0"
  style="border-radius: ${settings.borderRadius}; max-width: ${settings.maxWidth}; display: block; margin: ${settings.margin};"
  loading="lazy">
</iframe>`;
  }

  function getEmbedCode() {
    return settings.embedType === 'script' ? generateScriptEmbed() : generateIframeEmbed();
  }

  function copyToClipboard() {
    const code = getEmbedCode();
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleSave() {
    if (onSave) {
      onSave(settings);
    }
  }

  return (
    <div className="fb-modal-overlay">
      <div className="fb-modal embed">
        <div className="fb-embed-header">
          <h2 className="fb-modal-title">Embed Code Settings</h2>
          <button
            type="button"
            className="fb-embed-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="fb-grid-2">
          {/* Settings Panel */}
          <div>
            <h3 className="fb-subsection-title" style={{ marginBottom: '1rem' }}>Customization Options</h3>

            {/* Embed Type */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Embed Type
              </label>
              <select
                className="fb-select"
                value={settings.embedType}
                onChange={(e) => updateSetting('embedType', e.target.value)}
              >
                <option value="script">Script Tag (Recommended, auto-fit + tracking)</option>
                <option value="iframe">iFrame (fixed size / strict sites)</option>
              </select>
            </div>

            {/* Width */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Width
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="fb-input"
                  value={settings.width}
                  onChange={(e) => updateSetting('width', e.target.value)}
                  placeholder="100% or 600px"
                  style={{ flex: 1 }}
                />
                <select
                  className="fb-select"
                  value={settings.width.includes('%') ? 'percent' : 'pixels'}
                  onChange={(e) => {
                    const num = settings.width.replace(/[^0-9]/g, '') || '100';
                    updateSetting('width', e.target.value === 'percent' ? `${num}%` : `${num}px`);
                  }}
                  style={{ width: 'auto' }}
                >
                  <option value="percent">%</option>
                  <option value="pixels">px</option>
                </select>
              </div>
            </div>

            {/* Max Width */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Max Width (prevents form from being too wide)
              </label>
              <input
                type="text"
                className="fb-input"
                value={settings.maxWidth}
                onChange={(e) => updateSetting('maxWidth', e.target.value)}
                placeholder="600px"
              />
            </div>

            {/* Height (for iframe) */}
            {settings.embedType === 'iframe' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="fb-label">
                  Height
                </label>
                <input
                  type="text"
                  className="fb-input"
                  value={settings.height}
                  onChange={(e) => updateSetting('height', e.target.value)}
                  placeholder="600 or auto"
                />
              </div>
            )}

            {/* Responsive */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-radio-label">
                <input
                  type="checkbox"
                  checked={settings.responsive}
                  onChange={(e) => updateSetting('responsive', e.target.checked)}
                />
                <span>Fully Responsive (Auto-scales for mobile/tablet/desktop)</span>
              </label>
            </div>

            {/* Border Radius */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Border Radius
              </label>
              <input
                type="text"
                className="fb-input"
                value={settings.borderRadius}
                onChange={(e) => updateSetting('borderRadius', e.target.value)}
                placeholder="8px"
              />
            </div>

            {/* Padding */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Padding
              </label>
              <input
                type="text"
                className="fb-input"
                value={settings.padding}
                onChange={(e) => updateSetting('padding', e.target.value)}
                placeholder="2rem"
              />
            </div>

            {/* No scrolling area */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-radio-label">
                <input
                  type="checkbox"
                  checked={!!settings.noScroll}
                  onChange={(e) => updateSetting('noScroll', e.target.checked)}
                />
                <span>No scrolling area (form won&apos;t show a scrollbar when embedded)</span>
              </label>
              <div className="fb-muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                Use a tall enough iframe height so all content is visible, or content may be clipped.
              </div>
            </div>
          </div>

          {/* Preview & Code */}
          <div>
            <h3 className="fb-subsection-title" style={{ marginBottom: '1rem' }}>Preview & Code</h3>

            {/* Preview Box */}
            <div className="fb-embed-preview-box">
              <div style={{
                width: settings.width === '100%' ? '90%' : settings.width,
                maxWidth: settings.maxWidth,
                background: settings.backgroundColor,
                padding: settings.padding,
                borderRadius: settings.borderRadius,
                border: settings.border ? '1px solid var(--hairline-5)' : 'none',
                textAlign: 'center',
              }}>
                <div className="fb-muted" style={{ fontSize: '0.9rem' }}>
                  Form Preview
                </div>
                <div className="fb-faint" style={{ marginTop: '0.5rem' }}>
                  {settings.width} × {settings.maxWidth} max
                </div>
              </div>
            </div>

            {/* Platform picker */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Your platform
              </label>
              <select
                className="fb-select"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                style={{ marginBottom: '0.75rem' }}
              >
                {Object.entries(PLATFORM_INSTRUCTIONS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>

              <div className="fb-callout info" style={{ marginBottom: '0.5rem' }}>
                <strong>How to paste:</strong> {PLATFORM_INSTRUCTIONS[platform].steps}
              </div>

              <div className="fb-callout warn">
                It must be an <strong>HTML/embed block</strong>. If you see the code as text on your page, it is in the wrong block type.
              </div>
            </div>

            {/* Embed Code */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="fb-label">
                Embed Code
              </label>
              <textarea
                className="fb-textarea"
                value={getEmbedCode()}
                readOnly
                style={{ minHeight: '150px' }}
              />
            </div>

            {/* Actions */}
            <div className="fb-embed-actions">
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={copyToClipboard}
              >
                {copied ? 'Copied!' : 'Copy Code'}
              </button>
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={handleSave}
              >
                Save Settings
              </button>
            </div>

            <div className="fb-callout tip" style={{ marginTop: '1rem' }}>
              <strong>Host-page conversion tracking:</strong> On successful submission the embed emits{' '}
              <code>gocta:submit-success</code> (also accepted as <code>smartforms:submit-success</code>).
              Listen on your page with{' '}
              <code>{`window.addEventListener('gocta:submit-success', (e) => { /* e.detail.formId, leadId, submissionId, email? */ })`}</code>.
              {' '}Email is included only when you add <code>data-emit-lead-data=&quot;true&quot;</code> to the embed script tag.
              Fires once per real submission — not on validation errors or spam sinkhole responses.
            </div>

            <div className="fb-callout tip" style={{ marginTop: '1rem' }}>
              <strong>Tip:</strong> Forms automatically scale for all devices. The max-width ensures your form doesn&apos;t get too wide on large screens.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
